import { useEffect, useRef } from 'react';
import useCustomHook from '@/hooks/useCustomHook';
import { useGamificationContext } from '@/shared/Gamification/GamificationContext/GamificationContext';
import useActivitySEO from '../../useActivitySEO.hook';

const GAMIFICATION_PROGRESS_MILESTONES = [25, 50, 75, 100];
export default function useLegacyGameActivity({
  frame,
  activity,
  activityConf,
  taskProgressActions,
  demoMode,
  forceRestart,
  gameType,
  fullscreenContext,
}) {
  const gameOrigin = urlsByType[gameType];
  const gamificationContext = useGamificationContext();
  const milestones = useRef([]);
  const { actions: seoActions } = useActivitySEO({ activity, activityConf });
  const { state, actions } = useCustomHook({
    defaultState: {
      gameReady: false,
      activityId: null,
      miniactivityType: null,
      miniactivityLevel: null,
      iframeKey: 0,
      gameSrc: `${gameOrigin.url}?version=${gameOrigin.version}`,
      gameListenerReady: false,
      iframeStarsOrigin: null,
    },
    actions: {
      onIframeLoad: () => {
        console.log('Sending game version:', gameOrigin.version);
        frame.current.contentWindow.postMessage(
          {
            message: 'VERSIONCACHEBUST',
            payload: { version: gameOrigin.version },
            source: 'rockalingua',
          },
          '*'
        );
      },
    },
  });

  useEffect(() => {
    // Subscribe to game iframe messages
    const onGameMessage = ({ data, origin }) => {
      if (data.source == 'game' && gameOrigin.url.includes(origin)) {
        if (data.message == 'READY') {
          actions.setState({
            gameReady: true,
            activityId: activity.id,
            miniactivityType: activityConf?.miniactivityType,
            miniactivityLevel: activityConf?.level,
          });
        }
        const messageHandler = taskProgressAPI[data.message];
        if (messageHandler) {
          messageHandler(data.payload);
        }
      }
    };
    window.addEventListener('message', onGameMessage);
    actions.setState({ gameListenerReady: true });

    return () => {
      actions.setState({ gameListenerReady: false });
      window.removeEventListener('message', onGameMessage);
    };
  }, [taskProgressActions, state.gameReady, gamificationContext.state]);

  useEffect(() => {
    // Only if a miniactivity of a task
    if (activityConf) {
      if (
        activity.id != state.activityId ||
        activityConf?.miniactivityType != state.miniactivityType ||
        activityConf?.level != state.miniactivityLevel
      ) {
        actions.setState({ gameReady: false });
      }
    }
  }, [
    activity.id,
    activityConf?.miniactivityType,
    activityConf?.level,
    state.activityId,
    state.miniactivityType,
  ]);

  useEffect(() => {
    if (forceRestart) {
      taskProgressActions.onActivityRestarted();
      // Changing the iframe key forces remounting the game wrapper for the iframe game
      actions.setState({ iframeKey: state.iframeKey + 1, gameReady: false });
    }
  }, [forceRestart]);

  useEffect(() => {
    if (state.gameReady) {
      frame.current.contentWindow.postMessage(
        {
          message: 'CONFIGURATION',
          payload: {
            activity,
            activityConf,
            demoMode,
            version: urlsByType[gameType].version,
          },
          source: 'rockalingua',
        },
        '*'
      );
    }
  }, [state.gameReady]);

  useEffect(() => {
    frame.current?.contentWindow?.postMessage(
      {
        message: 'FULLSCREEN_CHANGED',
        payload: { isFullscreen: fullscreenContext.state.isFullscreen },
        source: 'rockalingua',
      },
      '*'
    );
  }, [fullscreenContext.state.isFullscreen]);

  const taskProgressAPI = {
    START: async () => {
      console.log('START MESSAGE RECEIVED');
      milestones.current = [];
      if (activityConf && taskProgressActions) {
        const progressData = {
          activitySubType: activity.data.subType,
          miniactivityType: activityConf.miniactivityType,
          progress: 0,
        };
        await taskProgressActions.createProgress(progressData);
      }

      seoActions.onStart();

      frame.current?.contentWindow.postMessage(
        {
          message: 'START_PROCESSED',
          source: 'rockalingua',
        },
        '*'
      );
    },
    PROGRESS: async (payload) => {
      console.log('PROGRESS MESSAGE RECEIVED', payload);
      if (activityConf) {
        if (taskProgressActions && !payload.skipTaskProgress) {
          if (activityConf.progress) {
            // Avoid sending progress on short stories story minicativity on the first page (overwrites progress on the second repetition)
            if (
              activityConf.miniactivityType == 'story' &&
              payload.progress <= 10
            ) {
              return;
            }

            const progressData = {
              activitySubType: activity.data.subType,
              miniactivityType: activityConf.miniactivityType,
              progress: payload.progress,
            };
            if (activityConf.correctness) {
              progressData.correct = payload.correct;
              progressData.answers = payload.answers;
            }
            await taskProgressActions.updateProgress(progressData);
          }
        }
      }

      if (!payload.skipGamificationProgress) {
        let correctAnswers = [];
        console.log('LEGACY starsOrigin FRONT', payload);
        actions.setState({ iframeStarsOrigin: payload.starsOrigin });
        if (payload.correctAnswers) {
          correctAnswers = payload.correctAnswers;
        } else {
          const nextMilestone = GAMIFICATION_PROGRESS_MILESTONES.find(
            (_, index) =>
              milestones.current.length == 0
                ? index == 0
                : index > milestones.current.length - 1
          );
          if (payload.progress >= nextMilestone) {
            correctAnswers = [nextMilestone];
            milestones.current = [...milestones.current, nextMilestone];
          }
        }

        if (payload.progress >= 100) {
          console.log('seo_PROGRESS_100');
          seoActions.onFinish();
        }
        gamificationContext.actions.putData({
          activityId: activity.id,
          miniactivityType: payload.miniactivityType,
          level: payload.miniactivityLevel,
          progress: payload.progress,
          correctAnswers,
        });
      }

      frame.current?.contentWindow.postMessage(
        {
          message: 'PROGRESS_PROCESSED',
          source: 'rockalingua',
        },
        '*'
      );
    },
    GAMEOVER: async (payload) => {
      console.log('GAMEOVER MESSAGE RECEIVED');
      if (activityConf && activityConf.progress) {
        const progressData = {
          activitySubType: activity.data.subType,
          miniactivityType: activityConf.miniactivityType,
          progress: payload.progress,
          gameOver: true,
        };
        if (activityConf.correctness) {
          progressData.correct = payload.correct;
          progressData.answers = payload.answers;
        }
        if (taskProgressActions) {
          await taskProgressActions.updateProgress({
            ...progressData,
            gameOver: true,
          });
        }
      }
      console.log('seo_FINISH');
      seoActions.onFinish();

      frame.current?.contentWindow.postMessage(
        {
          message: 'GAMEOVER_PROCESSED',
          source: 'rockalingua',
        },
        '*'
      );
    },
  };
  return { state, actions };
}
const urlsByType = {
  bubbles: {
    url: `${process.env.NEXT_PUBLIC_GAMES_URL_BUBBLES}/bubbles/${
      process.env.NEXT_PUBLIC_GAMES_URL_BUBBLES.includes(
        process.env.NEXT_PUBLIC_AWS_CLOUDFRONT_HOST
      )
        ? 'index.html'
        : ''
    }`,
    version: '20241031',
  },
  imagesQuiz: {
    url: `${process.env.NEXT_PUBLIC_GAMES_URL_IMAGEQUIZ}/imageQuiz/${
      process.env.NEXT_PUBLIC_GAMES_URL_IMAGEQUIZ.includes(
        process.env.NEXT_PUBLIC_AWS_CLOUDFRONT_HOST
      )
        ? 'index.html'
        : ''
    }`,
    version: '20241108',
  },
  quiz: {
    url: `${process.env.NEXT_PUBLIC_GAMES_URL_QUESTIONSQUIZ}/questionsQuiz/${
      process.env.NEXT_PUBLIC_GAMES_URL_QUESTIONSQUIZ.includes(
        process.env.NEXT_PUBLIC_AWS_CLOUDFRONT_HOST
      )
        ? 'index.html'
        : ''
    }`,
    version: '20241108',
  },
  shortStory: {
    url: `${process.env.NEXT_PUBLIC_GAMES_URL_SHORTSTORY}/shortStory/${
      process.env.NEXT_PUBLIC_GAMES_URL_SHORTSTORY.includes(
        process.env.NEXT_PUBLIC_AWS_CLOUDFRONT_HOST
      )
        ? 'index.html'
        : ''
    }`,
    version: '20241114',
  },
  skate: {
    url: `${process.env.NEXT_PUBLIC_GAMES_URL_SKATEBOARD}/skateboard/${
      process.env.NEXT_PUBLIC_GAMES_URL_SKATEBOARD.includes(
        process.env.NEXT_PUBLIC_AWS_CLOUDFRONT_HOST
      )
        ? 'index.html'
        : ''
    }`,
    version: '20241031',
  },
};
